import { invoiceTabFromSlug } from './invoiceRoutes';
import {
  SETTINGS_PATH_ACTIVITY_TYPES,
  SETTINGS_PATH_CHECKLISTS,
  SETTINGS_PATH_BUSINESS,
  SETTINGS_PATH_DOCUMENT_NUMBERING,
  SETTINGS_PATH_DOCUMENT_TEXTS,
  SETTINGS_PATH_EMAIL_DISPATCH,
  SETTINGS_PATH_INVOICE_FORWARDING,
  SETTINGS_PATH_LEAD_PIPELINE,
  SETTINGS_PATH_LEGAL_ATTACHMENTS,
  SETTINGS_PATH_LABOR_COST_SHARE,
  SETTINGS_PATH_OFFERS_INVOICES,
  SETTINGS_PATH_INTEGRATIONS,
  SETTINGS_PATH_DATEV,
  SETTINGS_PATH_INTEGRATION_IDS,
  SETTINGS_PATH_INTEGRATION_METAL,
  SETTINGS_PATH_INTEGRATION_OMD,
  SETTINGS_PATH_OMD,
  SETTINGS_PATH_OMD_GC,
  SETTINGS_PATH_IDS,
  SETTINGS_PATH_PAYMENT_CONDITIONS,
  SETTINGS_PATH_NOTIFICATIONS,
  SETTINGS_PATH_PERSONAL,
  SETTINGS_PATH_PLANTAFEL,
  SETTINGS_PATH_USERS,
  SETTINGS_PATH_USERS_API,
} from './settingsRoutes';

export const APP_BRAND = 'Meisterpfad';

const MAX_PRIMARY_SEGMENT_LENGTH = 60;

const INVOICE_TAB_LABELS: Record<string, string> = {
  outgoing: 'Ausgangsrechnungen',
  quotes: 'Angebote',
  incoming: 'Eingangsrechnungen',
};

const SETTINGS_PATH_LABELS: Record<string, string> = {
  [SETTINGS_PATH_BUSINESS]: 'Unternehmensangaben',
  [SETTINGS_PATH_USERS]: 'Nutzer',
  [SETTINGS_PATH_USERS_API]: 'Externe API',
  [SETTINGS_PATH_INTEGRATIONS]: 'Integrationen',
  [SETTINGS_PATH_DATEV]: 'DATEV',
  [SETTINGS_PATH_INTEGRATION_OMD]: 'Open Master Data',
  [SETTINGS_PATH_INTEGRATION_IDS]: 'IDS',
  [SETTINGS_PATH_INTEGRATION_METAL]: 'Metallnotierungen',
  [SETTINGS_PATH_OMD]: 'Integrationen',
  [SETTINGS_PATH_OMD_GC]: 'Integrationen',
  [SETTINGS_PATH_IDS]: 'Integrationen',
  [SETTINGS_PATH_DOCUMENT_TEXTS]: 'Textvorlagen',
  [SETTINGS_PATH_LEAD_PIPELINE]: 'Projekt-Pipeline',
  [SETTINGS_PATH_ACTIVITY_TYPES]: 'Aktivitätstypen',
  [SETTINGS_PATH_CHECKLISTS]: 'Checklisten',
  [SETTINGS_PATH_PLANTAFEL]: 'Plantafel',
  [SETTINGS_PATH_OFFERS_INVOICES]: 'Angebote & Rechnungen',
  [SETTINGS_PATH_PAYMENT_CONDITIONS]: 'Angebote & Rechnungen',
  [SETTINGS_PATH_LABOR_COST_SHARE]: 'Angebote & Rechnungen',
  [SETTINGS_PATH_LEGAL_ATTACHMENTS]: 'Angebote & Rechnungen',
  [SETTINGS_PATH_INVOICE_FORWARDING]: 'Rechnungsweiterleitung',
  [SETTINGS_PATH_DOCUMENT_NUMBERING]: 'Nummernkreise',
  [SETTINGS_PATH_PERSONAL]: 'Profil',
  [SETTINGS_PATH_NOTIFICATIONS]: 'Benachrichtigungen',
  [SETTINGS_PATH_EMAIL_DISPATCH]: 'E-Mail Versand',
};

function truncateSegment(value: string, maxLength = MAX_PRIMARY_SEGMENT_LENGTH): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
}

/** Build `Part1 · Part2 · Meisterpfad` from non-empty segments. */
export function formatDocumentTitle(...parts: Array<string | null | undefined>): string {
  const segments = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .map((part, index) => (index === 0 ? truncateSegment(part) : part));

  if (segments.length === 0) return APP_BRAND;
  return [...segments, APP_BRAND].join(' · ');
}

/** Strip `/i/:instanceId` prefix for route matching. */
function plainInstancePath(pathname: string): string {
  return pathname.replace(/^\/i\/[^/]+/, '') || '/';
}

/**
 * Static browser tab label from the current URL (before detail-page overrides).
 * Returns the primary segment only — `formatDocumentTitle` appends the brand.
 */
export function resolveDocumentTitleFromPath(pathname: string, _search = ''): string {
  const plain = plainInstancePath(pathname);

  if (pathname === '/login') return 'Anmelden';
  if (pathname === '/register') return 'Registrieren';
  if (pathname === '/auth/callback' || pathname === '/auth/confirm') return 'Anmeldung';
  if (pathname === '/auth/complete-profile') return 'Profil vervollständigen';
  if (pathname === '/auth/reset-password') return 'Passwort zurücksetzen';
  if (pathname === '/pending-approval') return 'Freigabe ausstehend';
  if (pathname === '/') return 'Anmelden';

  if (plain === '/dashboard') return 'Übersicht';
  if (plain === '/chat' || plain.startsWith('/chat/')) return 'KI Chat';
  if (plain === '/notifications') return 'Benachrichtigungen';

  if (plain === '/projects/pipeline' || plain === '/projects/list' || plain === '/projects/dashboard') {
    return 'Projekte';
  }
  if (plain === '/projects' || plain === '/leads') return 'Projekte';
  if (plain.startsWith('/projects/') || plain.startsWith('/leads/')) return 'Projekte';

  if (plain === '/inbox' || plain.startsWith('/inbox/')) return 'Posteingang';
  if (plain.startsWith('/calendar')) return 'Kalender';
  if (plain === '/todos' || plain.startsWith('/todos/')) return 'To-dos';

  if (plain === '/contacts' || plain.startsWith('/contacts/')) return 'Kontakte';
  if (plain.startsWith('/organizations/')) return 'Organisation';

  if (plain.startsWith('/invoices/')) {
    const tabSlug = plain.split('/')[2];
    const tab = invoiceTabFromSlug(tabSlug);
    if (tab) return INVOICE_TAB_LABELS[tab] ?? 'Rechnungen';
    return 'Rechnungen';
  }
  if (plain === '/invoices' || plain.startsWith('/rechnungen')) return 'Rechnungen';

  if (plain.startsWith('/settings')) {
    const settingsPath = plain.replace(/\/$/, '') || '/settings';
    return SETTINGS_PATH_LABELS[settingsPath] ?? 'Einstellungen';
  }

  if (plain === '/stammdaten' || plain.startsWith('/stammdaten/')) return 'Stammdaten';
  if (plain === '/templates') return 'Vorlagen';
  if (plain.startsWith('/mitarbeiter')) return 'Mitarbeiterverwaltung';

  if (plain === '/ausschreibungen') return 'Ausschreibungen';
  if (plain.startsWith('/ausschreibungen/')) return 'Ausschreibungen';
  if (plain === '/fleet' || plain.startsWith('/fleet/')) return 'Fuhrpark';

  return '';
}
